import './beta.css';
import { alertUser, authHeaders, telegramPlatform } from './telegram';

declare const __APP_VERSION__: string;

const LAST_JOB_KEY = 'krug_beta_last_job';
const LAST_ERROR_KEY = 'krug_beta_last_error';

interface LastError {
  stage: string;
  message: string;
  at: string;
}

interface BetaStatus {
  ok: true;
  version: string;
  conversionsPaused: boolean;
  reportsLast24h: number;
}

let paused = false;

function storageGet(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function storageSet(key: string, value: string): void {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // Diagnostics must never break the editor when storage is unavailable.
  }
}

function rememberError(stage: string, message: string): void {
  const value: LastError = {
    stage: stage.slice(0, 140),
    message: message.slice(0, 900),
    at: new Date().toISOString(),
  };
  storageSet(LAST_ERROR_KEY, JSON.stringify(value));
}

function readLastError(): LastError | null {
  const raw = storageGet(LAST_ERROR_KEY);
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as LastError;
    return parsed && typeof parsed.stage === 'string' && typeof parsed.message === 'string'
      ? parsed
      : null;
  } catch {
    return null;
  }
}

function inputUrl(input: RequestInfo | URL): URL | null {
  try {
    if (typeof input === 'string') {
      return new URL(input, window.location.href);
    }
    if (input instanceof URL) {
      return input;
    }
    return new URL(input.url, window.location.href);
  } catch {
    return null;
  }
}

function inputMethod(input: RequestInfo | URL, init?: RequestInit): string {
  if (init?.method) {
    return init.method.toUpperCase();
  }
  return input instanceof Request ? input.method.toUpperCase() : 'GET';
}

function installFetchProbe(): void {
  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url = inputUrl(input);
    const method = inputMethod(input, init);
    let response: Response;
    try {
      response = await originalFetch(input, init);
    } catch (error) {
      if (url?.pathname.startsWith('/api/')) {
        rememberError(
          `${method} ${url.pathname}`,
          error instanceof Error ? error.message : 'Network request failed',
        );
      }
      throw error;
    }

    if (url?.pathname === '/api/conversions' && method === 'POST' && response.ok) {
      void response
        .clone()
        .json()
        .then((payload: unknown) => {
          const jobId =
            payload && typeof payload === 'object'
              ? (payload as { jobId?: unknown }).jobId
              : undefined;
          if (typeof jobId === 'string' && jobId) {
            storageSet(LAST_JOB_KEY, jobId);
          }
        })
        .catch(() => undefined);
    }

    if (url?.pathname === '/api/client-errors' && method === 'POST' && typeof init?.body === 'string') {
      try {
        const body = JSON.parse(init.body) as { stage?: unknown; message?: unknown };
        if (typeof body.stage === 'string' && typeof body.message === 'string') {
          rememberError(body.stage, body.message);
        }
      } catch {
        // Ignore malformed diagnostic payloads.
      }
    }

    if (url?.pathname.startsWith('/api/') && !response.ok && url.pathname !== '/api/beta-status') {
      rememberError(`${method} ${url.pathname}`, `HTTP ${response.status}`);
    }
    return response;
  };
}

function textContent(selector: string): string {
  return document.querySelector<HTMLElement>(selector)?.textContent?.trim() ?? '';
}

function activeTab(): string {
  return document.querySelector<HTMLButtonElement>('[data-tab].is-active')?.dataset.tab ?? 'unknown';
}

function reportContext(): Record<string, unknown> {
  const source = [textContent('#sourceName'), textContent('#sourceMeta')].filter(Boolean).join(' · ');
  const result = [textContent('#resultTitle'), textContent('#resultText')].filter(Boolean).join(' · ');
  const lastError = readLastError();
  return {
    source,
    result,
    lastError: lastError ? `${lastError.at} · ${lastError.stage} · ${lastError.message}` : '',
    pathname: window.location.pathname,
    viewport: `${window.innerWidth}×${window.innerHeight} @${window.devicePixelRatio || 1}`,
    language: navigator.language,
    online: navigator.onLine,
  };
}

function currentStage(): string {
  const lastError = readLastError();
  const progress = textContent('#progressText');
  return lastError?.stage || progress || activeTab();
}

function createBetaUi(): {
  overlay: HTMLElement;
  reportButton: HTMLButtonElement;
  form: HTMLFormElement;
  textarea: HTMLTextAreaElement;
  status: HTMLElement;
  closeButton: HTMLButtonElement;
  banner: HTMLElement;
} {
  const footer = document.createElement('footer');
  footer.className = 'beta-footer';

  const reportButton = document.createElement('button');
  reportButton.type = 'button';
  reportButton.className = 'beta-footer-button';
  reportButton.textContent = 'Сообщить о проблеме';

  const separator = document.createElement('span');
  separator.className = 'beta-footer-separator';
  separator.textContent = '·';

  const version = document.createElement('span');
  version.className = 'beta-version';
  version.textContent = `v${__APP_VERSION__}`;
  version.title = 'Версия приложения';
  footer.append(reportButton, separator, version);
  document.body.append(footer);

  const banner = document.createElement('div');
  banner.className = 'beta-banner';
  banner.hidden = true;
  banner.textContent = 'Техническая пауза: новые кружки временно не запускаются. Уже начатые операции могут завершиться.';
  const header = document.querySelector('header.hero');
  header?.insertAdjacentElement('afterend', banner);

  const overlay = document.createElement('section');
  overlay.className = 'beta-report-overlay';
  overlay.hidden = true;
  overlay.innerHTML = `
    <div class="beta-report-card" role="dialog" aria-modal="true" aria-labelledby="betaReportTitle">
      <div class="beta-report-heading">
        <div>
          <h2 id="betaReportTitle">Сообщить о проблеме</h2>
          <p>Опишите, что произошло и чего вы ожидали. Технические данные приложатся автоматически.</p>
        </div>
        <button class="beta-report-close" type="button" aria-label="Закрыть">×</button>
      </div>
      <form class="beta-report-form">
        <label for="betaReportText">Что произошло</label>
        <textarea id="betaReportText" maxlength="1200" required placeholder="Например: выбрал MOV-рамку, нажал «Сделать кружок», а на этапе отправки появилась ошибка…"></textarea>
        <small>К репорту прикладываются версия, платформа, последний job ID, этап операции и последняя техническая ошибка. Само видео не отправляется.</small>
        <div class="beta-report-status" role="status" aria-live="polite"></div>
        <button class="primary-button beta-report-submit" type="submit">Отправить репорт</button>
      </form>
    </div>`;
  document.body.append(overlay);

  const form = overlay.querySelector<HTMLFormElement>('.beta-report-form');
  const textarea = overlay.querySelector<HTMLTextAreaElement>('#betaReportText');
  const status = overlay.querySelector<HTMLElement>('.beta-report-status');
  const closeButton = overlay.querySelector<HTMLButtonElement>('.beta-report-close');
  if (!form || !textarea || !status || !closeButton) {
    throw new Error('Beta report UI failed to initialize.');
  }
  return { overlay, reportButton, form, textarea, status, closeButton, banner };
}

function setPausedState(value: boolean, banner: HTMLElement): void {
  paused = value;
  document.body.classList.toggle('beta-paused', paused);
  banner.hidden = !paused;
}

async function loadBetaStatus(banner: HTMLElement): Promise<void> {
  try {
    const response = await fetch('/api/beta-status', { cache: 'no-store' });
    const payload = (await response.json()) as BetaStatus;
    if (!response.ok || payload.ok !== true) {
      return;
    }
    setPausedState(payload.conversionsPaused, banner);
    if (payload.version && payload.version !== __APP_VERSION__) {
      console.warn('Client/server version mismatch', {
        client: __APP_VERSION__,
        server: payload.version,
      });
    }
  } catch {
    // The editor remains usable if the optional beta status endpoint is unavailable.
  }
}

function installPausedClickGuard(): void {
  document.addEventListener(
    'click',
    (event) => {
      if (!paused) {
        return;
      }
      const target = event.target instanceof Element ? event.target : null;
      const blocked = target?.closest('#convertButton, #resendResult, a[href*="tribute.tg"]');
      if (!blocked) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      alertUser('Сейчас включена техническая пауза. Новые операции временно не запускаются.');
    },
    true,
  );
}

function initializeUi(): void {
  const { overlay, reportButton, form, textarea, status, closeButton, banner } = createBetaUi();
  const close = (): void => {
    overlay.hidden = true;
    document.body.classList.remove('beta-report-open');
  };
  const open = (): void => {
    status.textContent = '';
    overlay.hidden = false;
    document.body.classList.add('beta-report-open');
    window.setTimeout(() => textarea.focus(), 0);
  };

  reportButton.addEventListener('click', open);
  closeButton.addEventListener('click', close);
  overlay.addEventListener('click', (event) => {
    if (event.target === overlay) {
      close();
    }
  });

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    const message = textarea.value.trim();
    if (!message) {
      return;
    }
    const submit = form.querySelector<HTMLButtonElement>('button[type="submit"]');
    if (!submit) {
      return;
    }
    submit.disabled = true;
    status.textContent = 'Отправляем…';
    try {
      const headers = new Headers(authHeaders());
      headers.set('Content-Type', 'application/json');
      const response = await fetch('/api/beta-reports', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          message,
          appVersion: __APP_VERSION__,
          jobId: storageGet(LAST_JOB_KEY),
          stage: currentStage(),
          platform: telegramPlatform(),
          tab: activeTab(),
          context: reportContext(),
        }),
      });
      const payload = (await response.json().catch(() => null)) as
        | { ok: true; reportId: string }
        | { ok?: false; message?: string }
        | null;
      if (!response.ok || !payload || payload.ok !== true) {
        throw new Error(payload && 'message' in payload && payload.message ? payload.message : 'Не удалось отправить репорт.');
      }
      textarea.value = '';
      close();
      alertUser(`Репорт ${payload.reportId} отправлен. Спасибо.`);
    } catch (error) {
      status.textContent = error instanceof Error ? error.message : 'Не удалось отправить репорт.';
    } finally {
      submit.disabled = false;
    }
  });

  installPausedClickGuard();
  void loadBetaStatus(banner);
}

installFetchProbe();
window.addEventListener('error', (event) => {
  rememberError('window.error', event.message || 'Unknown browser error');
});
window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason;
  rememberError(
    'unhandledrejection',
    reason instanceof Error ? `${reason.name}: ${reason.message}` : String(reason ?? 'Unknown rejection'),
  );
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeUi, { once: true });
} else {
  initializeUi();
}
